import { parseDateString } from '@utils-global/date-time';
import { Nullable } from '@models-global/constants';
import { Values } from '@utils-global/type-values';
import { ModelBase } from '@models-global/model-base';
import { ENUM_ROLES } from '../../module-role/enums/enum-roles';
import { ENUM_USER_STATUS } from '@module-user/enums/enum-user-status';
import { ModelCustomer } from '@module-customer/models/model-customer';
import { ModelManufacturer } from '@module-manufacturer/model/model-manufacturer';
import { CoveredEntityAccount } from '@module-covered-entity/models/ce-account';
import { ThirdPartyAdministrator } from '@module-tpa/models/model-tpa';
import { NavMenuConfiguration } from '@models-global/nav-menu-configuration.interface';
import { environment } from '@environment/environment';
import { FeatureMode } from '@environment/environment.interface';
import { AuditCustomerType } from '@module-customer/enums/audit-customer-type.enum';
import {
  isAdminUser,
  isRxpUser,
  isRxpAdminUser,
  isManufacturerUser,
  isCustomerUser,
  isAuditCustomerUser,
  isCustomerReporterUser,
  isCEUser,
  isTPAUser,
  isAccess,
  isCEAdminUser,
  isPharmacyUser,
  isRebateCustomerUser,
  isHealthSystemCustomerUser,
  isCEUserWithMFPRebate
} from '@module-role/utils';
import { MFPPharmacyClient } from '@module-mfp-pharmacy/models/mfp-pharmacy-client';
import {
  CUSTOMER_DETAILS_URL_DEFAULT,
  CUSTOMER_LIST_URL_AUDIT_340B,
  CUSTOMER_LIST_URL_DEFAULT,
  CUSTOMER_LIST_URL_HEALTH_SYSTEM,
  CUSTOMER_LIST_URL_PHARMACY_CHAIN
} from '@module-customer-management/constants/customer-route-constants';
import { CoveredEntityServiceType } from '@module-covered-entity/enums/ce-account.enum';

export interface User {
  readonly id: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly jobTitle: string | null;
  readonly phoneNumber: string | null;
  readonly email: string;
  readonly roles: Values<typeof ENUM_ROLES>[];
  readonly permissions: string[];
  readonly isNew: boolean;
  readonly lastActive: Date | string;
  readonly status: Values<typeof ENUM_USER_STATUS>;
  readonly picture: string;
  readonly initials: string;
  readonly createdAt: Nullable<Date>;
  readonly customers: ModelCustomer[];
  readonly manufacturers: ModelManufacturer[];
  readonly associated?: string;
  readonly coveredentities: CoveredEntityAccount[];
  readonly thirdpartyadministrators: ThirdPartyAdministrator[];
  readonly pharmacies: MFPPharmacyClient[];
  readonly freshdeskContactId: number | null;
  readonly isEntityContact?: boolean;
}

export class ModelUser implements ModelBase<User, ModelUser>, User {
  readonly id: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly jobTitle: string | null;
  readonly phoneNumber: string | null;
  readonly email: string;
  readonly isNew: boolean;
  readonly lastActive: Date;
  readonly status: Values<typeof ENUM_USER_STATUS>;
  readonly picture: string;
  readonly initials: string;
  readonly permissions: string[];
  readonly roles: Values<typeof ENUM_ROLES>[];
  readonly createdAt: Nullable<Date>;
  readonly customers: ModelCustomer[];
  readonly manufacturers: ModelManufacturer[];
  readonly associated?: string;
  readonly coveredentities: CoveredEntityAccount[];
  readonly thirdpartyadministrators: ThirdPartyAdministrator[];
  readonly pharmacies: MFPPharmacyClient[];
  readonly freshdeskContactId: number | null;
  readonly isEntityContact?: boolean;
  private _navMenuConfig?: NavMenuConfiguration;

  constructor({
    id = null,
    firstName = '',
    lastName = '',
    jobTitle = '',
    phoneNumber = '',
    email = '',
    isNew = false,
    lastActive = new Date(),
    status = ENUM_USER_STATUS.INACTIVE,
    picture = '',
    initials = '',
    permissions = [],
    roles = [],
    createdAt = null,
    customers = [],
    manufacturers = [],
    associated = '',
    coveredentities = [],
    thirdpartyadministrators = [],
    pharmacies = [],
    freshdeskContactId = null,
    isEntityContact = undefined
  }: Partial<User> = {}) {
    this.id = id;
    this.firstName = firstName;
    this.lastName = lastName;
    this.jobTitle = jobTitle;
    this.phoneNumber = phoneNumber;
    this.email = email;
    this.isNew = isNew;
    this.lastActive =
      typeof lastActive === 'string' ? new Date(lastActive) : lastActive;
    this.status = status;
    this.picture = picture;
    this.initials = initials;
    this.permissions = permissions;
    this.roles = roles;
    this.createdAt = !createdAt ? createdAt : parseDateString(createdAt);
    this.customers = customers;
    this.manufacturers = manufacturers;
    this.associated = associated;
    this.coveredentities = coveredentities;
    this.thirdpartyadministrators = thirdpartyadministrators;
    this.pharmacies = pharmacies;
    this.freshdeskContactId = freshdeskContactId;
    this.isEntityContact = isEntityContact;
  }

  serialize(): User {
    return {
      id: this.id,
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      isNew: this.isNew,
      jobTitle: this.jobTitle,
      phoneNumber: this.phoneNumber,
      roles: this.roles,
      //TODO cast to UTC
      lastActive: this.lastActive,
      status: this.status,
      picture: this.picture,
      initials: this.initials,
      permissions: this.permissions,
      createdAt: this.createdAt,
      customers: this.customers,
      manufacturers: this.manufacturers,
      coveredentities: this.coveredentities,
      thirdpartyadministrators: this.thirdpartyadministrators,
      pharmacies: this.pharmacies,
      freshdeskContactId: this.freshdeskContactId,
      isEntityContact: this.isEntityContact
    };
  }

  serializeToServer(): User {
    const override = {
      id: this.isNew || this.id === 'tempId' ? null : this.id
    };
    return { ...this.serialize(), ...override };
  }

  clone(): ModelUser {
    return new ModelUser(this.serialize());
  }

  private _generatePartialNavMenuConfig(
    config: Partial<NavMenuConfiguration>
  ): NavMenuConfiguration {
    const defaultConfig: NavMenuConfiguration = {
      home: { enabled: false },
      dashboard: { enabled: false },
      admin: { enabled: false },
      adminUserList: { enabled: false },
      adminAddUser: { enabled: false },
      hcpcs: { enabled: false },
      stateCarveIn: { enabled: false },
      stateDrugExclusion: { enabled: false },
      inboundSms: { enabled: false },
      mfg: { enabled: false },
      mfgList: { enabled: false },
      mfgAdd: { enabled: false },
      mfgPermissions: { enabled: false },
      mfgDrugLists: { enabled: false },
      mfgDetails: { enabled: false },
      customer: { enabled: false },
      customerList: { enabled: false },
      auditCustomerList: { enabled: false },
      healthcareProviderList: { enabled: false },
      pharmacyChainList: { enabled: false },
      customerAdd: { enabled: false },
      customerDetails: { enabled: false },
      tpa: { enabled: false },
      tpaList: { enabled: false },
      tpaAdd: { enabled: false },
      tpaDetails: { enabled: false },
      ce: { enabled: false },
      ceList: { enabled: false },
      ceDetails: { enabled: false },
      ceOnboarding: { enabled: false },
      ceDashboard: { enabled: false },
      mfpPharmacy: { enabled: false },
      mfpPharmacyList: { enabled: false },
      mfpPharmacyAdd: { enabled: false },
      mfpPharmacyDetails: { enabled: false },
      mfpDrugs: { enabled: false },
      mfpRebateFileMonitoring: { enabled: false },
      mfpRebateReports: { enabled: false },
      mfpManagement: { enabled: false },
      rebateManagement: { enabled: false },
      claims: { enabled: false },
      claimsUpload: { enabled: false },
      claimsUploadLog: { enabled: false },
      claimsReview: { enabled: false },
      contract: { enabled: false },
      contractList: { enabled: false },
      contractAdd: { enabled: false },
      bd: { enabled: false },
      bdList: { enabled: false },
      bdAdd: { enabled: false },
      mfp: { enabled: false },
      mfpClaims: { enabled: false },
      prescriberSearch: { enabled: false },
      pharmacySearch: { enabled: false },
      validateChargeback: { enabled: false },
      auditClaimSearch: { enabled: false },
      disputeCenter: { enabled: false },
      disputeDashboard: { enabled: false },
      claimReconciliation: { enabled: false },
      vendorConfiguration: { enabled: false },
      mfrSubmission: { enabled: false }
    };

    return {
      ...defaultConfig,
      ...config
    };
  }

  get navMenuConfig(): NavMenuConfiguration | undefined {
    if (!this.id || this._navMenuConfig) {
      return this._navMenuConfig;
    }

    const isAdmin = isAdminUser(this);
    const isRxp = isRxpUser(this);
    const isRxpAdmin = isRxpAdminUser(this);
    const isManufacturer = isManufacturerUser(this);
    const isCustomer = isCustomerUser(this);
    const isRebateCustomer = isRebateCustomerUser(this);
    const isAuditCustomer = isAuditCustomerUser(this);
    const isAuditCustomerManufacturer = isAuditCustomerUser(this, [
      AuditCustomerType.MANUFACTURER
    ]);
    const isCustomerReporter = isCustomerReporterUser(this);
    const isCE = isCEUser(this);
    const isCEWithMFPRebate = isCEUserWithMFPRebate(this);
    const isCEOnboarding =
      isCEAdminUser(this) && !this.coveredentities[0]?.onboarded;
    const isTPA = isTPAUser(this);
    const isPharmacy = isPharmacyUser(this);
    const isHealthSystem = isHealthSystemCustomerUser(this);
    const isCeAndPharmacyUser = isCE && isPharmacy;

    const ceMainServiceType = this.coveredentities[0]?.serviceType;
    const ceDashboardLabel =
      ceMainServiceType === CoveredEntityServiceType.CLEARINGHOUSE
        ? 'Clearinghouse Dashboard'
        : ceMainServiceType === CoveredEntityServiceType.REPOSITORY
        ? 'Repository Dashboard'
        : 'CE Dashboard';

    let navMenuConfig: NavMenuConfiguration = {
      home: {
        enabled:
          !isCustomerReporter &&
          !isCE &&
          !isTPA &&
          !isHealthSystem &&
          !isPharmacy,
        label: 'Home',
        link: '/'
      },
      dashboard: {
        enabled: isCE || isTPA || isHealthSystem || isPharmacy,
        label: 'Dashboard',
        link: '/dashboard'
      },
      admin: {
        enabled: isAdmin || isRxp,
        label: 'Admin'
      },
      adminUserList: {
        enabled: isAdmin || isRxp,
        label: 'User Management',
        link: '/user-management/list'
      },
      adminAddUser: {
        enabled: isAccess(this, {
          roles: [ENUM_ROLES.RXP_ADMIN, ENUM_ROLES.RXP_STAFF, ENUM_ROLES.ADMIN]
        }),
        label: 'Add User',
        link: '/user-management/user-add'
      },
      mfg: {
        enabled: isRxp || isManufacturer,
        label: 'Manufacturer'
      },
      mfgList: {
        enabled: isRxp,
        label: 'Manufacturer Management',
        link: '/manufacturer-management/list'
      },
      mfgAdd: {
        enabled: isRxp,
        label: 'Add Manufacturer',
        link: '/manufacturer-management/manufacturer-add'
      },
      mfgPermissions: {
        enabled: isRxp,
        label: 'Permissions',
        link: '/manufacturer-management/permissions'
      },
      mfgDrugLists: {
        enabled: isRxp,
        label: 'Drug Lists',
        link: '/manufacturer-management/drug-lists'
      },
      mfgDetails: {
        enabled: isManufacturer,
        label: 'Manufacturer Details',
        link: `/manufacturer-management/manufacturer-details/${this.manufacturers[0]?.code}`
      },
      customer: {
        enabled: isRxp || (isCustomer && !isCustomerReporter),
        label: 'Customer'
      },
      customerList: {
        enabled: isRxp,
        label: 'Rebate Customer',
        link: CUSTOMER_LIST_URL_DEFAULT
      },
      auditCustomerList: {
        enabled: isRxpAdmin,
        label: 'Audit Customer',
        link: CUSTOMER_LIST_URL_AUDIT_340B
      },
      healthcareProviderList: {
        enabled: isRxpAdmin,
        label: 'Health System',
        link: CUSTOMER_LIST_URL_HEALTH_SYSTEM
      },
      pharmacyChainList: {
        enabled: isRxpAdmin,
        label: 'Pharmacy Chain',
        link: CUSTOMER_LIST_URL_PHARMACY_CHAIN
      },
      customerAdd: {
        enabled: isRxp,
        label: 'Add Customer',
        link: CUSTOMER_DETAILS_URL_DEFAULT
      },
      customerDetails: {
        enabled: isCustomer && !isCustomerReporter,
        label: 'Customer Details',
        link: `${CUSTOMER_DETAILS_URL_DEFAULT}/${this.customers[0]?.code}`
      },
      tpa: {
        enabled: isRxpAdmin || isTPA,
        label: 'Third Party Administrator'
      },
      tpaList: {
        enabled: isRxpAdmin,
        label: 'TPA Management',
        link: '/tpa/list'
      },
      tpaAdd: {
        enabled: isRxpAdmin,
        label: 'Add TPA',
        link: '/tpa/details'
      },
      tpaDetails: {
        enabled: isTPA,
        label: 'TPA Details',
        link: `/tpa/details/${this.thirdpartyadministrators[0]?.id}`
      },
      ce: {
        enabled: isRxpAdmin || isCE,
        label: 'Covered Entity'
      },
      ceList: {
        enabled: isRxpAdmin,
        label: 'Covered Entity Management',
        link: '/ce/list'
      },
      ceDetails: {
        enabled: isCE,
        label: 'Covered Entity Details',
        link: `/ce/details/${this.coveredentities[0]?.id}`
      },
      ceOnboarding: {
        enabled: isCEOnboarding,
        label: 'Onboarding',
        link: `/ce/onboarding/${this.coveredentities[0]?.id}`
      },
      ceDashboard: {
        enabled: isCE && isCEWithMFPRebate && !isCeAndPharmacyUser,
        label: ceDashboardLabel,
        link: '/ce/dashboard'
      },
      mfpPharmacy: {
        enabled: isRxpAdmin || isPharmacy,
        label: 'MFP Pharmacy'
      },
      mfpPharmacyList: {
        enabled: isRxpAdmin,
        label: 'Pharmacy Management',
        link: '/mfp-pharmacy/list'
      },
      mfpPharmacyAdd: {
        enabled: isRxpAdmin,
        label: 'Add Pharmacy Client',
        link: '/mfp-pharmacy/details'
      },
      mfpPharmacyDetails: {
        enabled: isPharmacy,
        label: 'Pharmacy Client Details',
        link: `/mfp-pharmacy/details/${this.pharmacies[0]?.id}`
      },
      mfpDrugs: {
        enabled: (isRxpAdmin || isCE) && !isCeAndPharmacyUser,
        label: 'MFP Drug Management',
        link: '/drug-management/mfp-drugs'
      },
      mfpRebateFileMonitoring: {
        enabled: !!environment.enableDemoFeatures && (isRxpAdmin || isCE),
        label: 'File Monitoring',
        link: '/mfp-rebate/file-monitoring-dashboard'
      },
      mfpRebateReports: {
        enabled: isRxpAdmin || isCE || isPharmacy || isHealthSystem,
        label: 'Reports',
        link: '/mfp-rebate/reports-list'
      },
      mfpManagement: {
        enabled: false,
        label: 'MFP Management',
        link: '/mfp-rebate/mfp-management'
      },
      rebateManagement: {
        enabled: false,
        label: '340B Rebate Management',
        link: '/mfp-rebate/rebate-management'
      },
      contract: {
        enabled: isRxp || isManufacturer,
        label: 'Contract'
      },
      contractList: {
        enabled: isRxp || isManufacturer,
        label: 'View/Manage Contracts',
        link: '/contract-management/list'
      },
      contractAdd: {
        enabled: isRxp,
        label: 'Create Contract',
        link: '/contract-management/details'
      },
      bd: {
        enabled:
          isRxp || isManufacturer || (isRebateCustomer && !isCustomerReporter),
        label: 'Benefit Design'
      },
      bdList: {
        enabled:
          isRxp || isManufacturer || (isRebateCustomer && !isCustomerReporter),
        label: 'View/Manage Benefit Designs',
        link: '/benefit-designs-management'
      },
      bdAdd: {
        enabled: isRxp,
        label: 'Create Benefit Design',
        link: '/benefit-designs-management/create'
      },
      claims: {
        enabled: isRxp || isAuditCustomer || isCustomerReporter,
        label: isAuditCustomer ? '340B Audit' : 'Claims'
      },
      claimsUpload: {
        enabled: (isRxp || isAuditCustomer) && !isCustomerReporter,
        label: 'Upload Claims',
        link: '/claims/upload'
      },
      claimsUploadLog: {
        enabled: (isRxp || isAuditCustomer) && !isCustomerReporter,
        label: 'Upload Log',
        link: '/claims/upload-log'
      },
      claimsReview: {
        enabled: isRxp || isAuditCustomer || isCustomerReporter,
        label: 'Review Claims',
        link: '/claims/customer-selection'
      },
      mfp: {
        enabled: isAuditCustomerManufacturer && !isCustomerReporter,
        label: 'MFP'
      },
      mfpClaims: {
        enabled: isAuditCustomerManufacturer && !isCustomerReporter,
        label: 'Claims',
        link: '/claims/mfp'
      },
      hcpcs: {
        enabled: isRxp,
        label: 'HCPCS',
        link: '/hcpcs-management/rules'
      },
      stateCarveIn: {
        enabled: isRxp,
        label: 'State Carve-In',
        link: '/state-carve-in-management/rules'
      },
      stateDrugExclusion: {
        enabled: isRxp,
        label: 'State Drug Exclusion',
        link: '/state-drug-exclusion-management/list'
      },
      inboundSms: {
        enabled: isRxpAdmin,
        label: 'Inbound SMS',
        link: '/inbound-sms-management/list'
      },
      prescriberSearch: {
        enabled: isRxp,
        label: 'Prescriber Search'
      },
      pharmacySearch: {
        enabled: isRxp,
        label: 'Pharmacy Search'
      },
      validateChargeback: {
        enabled: (isRxp || isAuditCustomerManufacturer) && !isCustomerReporter,
        label: 'Validate Chargeback',
        link: '/invoice-management/validate-chargeback'
      },
      auditClaimSearch: {
        enabled: (isRxp || isAuditCustomerManufacturer) && !isCustomerReporter,
        label: '340B Claim Search'
      },
      disputeCenter: {
        enabled: environment.isDisputeLinkShowAllUser === true || isRxpAdmin,
        label: 'Dispute Center',
        link: '/mfp-pharmacy/claim-disputes-list'
      },
      disputeDashboard: {
        enabled: environment?.enableDemoFeatures === true,
        label: 'Dispute Dashboard',
        link: '/dispute-dashboard'
      },
      claimReconciliation: {
        enabled: isRxpAdmin,
        label: 'Unmatched 835 Claims',
        link: '/mfp-pharmacy/claim-reconciliation'
      },
      vendorConfiguration: {
        enabled: isRxpAdmin || isCE || isHealthSystem,
        label: 'Manufacturer Program Configuration',
        link: '/manufacturer-global-config-management/list'
      },
      mfrSubmission: {
        enabled: isRxpAdmin,
        label: 'Manufacturer Submission',
        link: '/manufacturer-global-config-management/manufacturer-submission'
      }
    };

    if (isCEOnboarding) {
      navMenuConfig = this._generatePartialNavMenuConfig({
        ce: navMenuConfig.ce,
        ceOnboarding: navMenuConfig.ceOnboarding
      });
    }

    switch (environment.featureMode) {
      case FeatureMode.REBATE:
        return {
          ...navMenuConfig,
          auditCustomerList: { enabled: false },
          healthcareProviderList: { enabled: false },
          pharmacyChainList: { enabled: false },
          tpa: { enabled: false },
          tpaList: { enabled: false },
          tpaAdd: { enabled: false },
          tpaDetails: { enabled: false },
          ce: { enabled: false },
          ceList: { enabled: false },
          ceDetails: { enabled: false },
          ceOnboarding: { enabled: false },
          ceDashboard: { enabled: false },
          mfpPharmacy: { enabled: false },
          mfpPharmacyList: { enabled: false },
          mfpPharmacyAdd: { enabled: false },
          mfpPharmacyDetails: { enabled: false },
          mfpDrugs: { enabled: false },
          mfp: { enabled: false },
          mfpClaims: { enabled: false },
          mfpRebateFileMonitoring: { enabled: false },
          mfpRebateReports: { enabled: false },
          mfpManagement: { enabled: false },
          rebateManagement: { enabled: false },
          stateCarveIn: { enabled: false },
          stateDrugExclusion: { enabled: false },
          inboundSms: { enabled: false },
          prescriberSearch: { enabled: false },
          pharmacySearch: { enabled: false },
          validateChargeback: { enabled: false },
          auditClaimSearch: { enabled: false },
          disputeCenter: { enabled: false },
          claimReconciliation: { enabled: false },
          vendorConfiguration: { enabled: false },
          mfrSubmission: { enabled: false }
        };
      case FeatureMode.AUDIT_340B:
        return {
          ...navMenuConfig,
          mfg: { enabled: false },
          mfgList: { enabled: false },
          mfgAdd: { enabled: false },
          mfgPermissions: { enabled: false },
          mfgDrugLists: { enabled: false },
          mfgDetails: { enabled: false },
          customerList: { enabled: false },
          contract: { enabled: false },
          contractList: { enabled: false },
          contractAdd: { enabled: false },
          bd: { enabled: false },
          bdList: { enabled: false },
          bdAdd: { enabled: false },
          hcpcs: { enabled: false }
        };
      default:
        return navMenuConfig;
    }
  }
}
